import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import axios from 'axios'
import Player from 'xgplayer'

// 時間轉換輔助函式
function timeAgo(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const seconds = Math.floor((now - past) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + " 年前";
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + " 個月前";
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + " 天前";
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + " 小時前";
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + " 分鐘前";
  }
  return Math.floor(seconds) + " 秒前";
}

export const usePostsStore = defineStore('posts', {
  state: () => ({
    // 基本設定
    posts_json: '/posts.json',
    ready: false,

    // 路由狀態
    currentPath: location.pathname,
    
    // 貼文資料
    posts: [],
    maybeMore: true,
    showNewest: false,
    
    // 單篇貼文詳情
    currentPost: null,
    currentComments: [],
    isLoadingDetail: false,
    
    // 搜尋功能
    search: false,
    keyword: '',
    
    // 通知數量
    notificationCount: 0,
    
    // 詢問跳轉閱讀位置的通知
    jumpNotification: null,
    showJumpNotification: false,
    
    // UI 狀態
    postHeight: 50,
    moreBtn: null,
    observer: null,
    postList: null,
    
    // 播放器
    player: []
  }),

  getters: {
    // 計算屬性可以在這裡定義
    postsCount: (state) => state.posts.length,
    hasMorePosts: (state) => state.maybeMore && state.posts.length > 0,
    
    // 基於響應性狀態的路由檢查
    isPostDetailPage: (state) => /^\/posts\/\d+$/.test(state.currentPath),
    currentPostId: (state) => {
      const match = state.currentPath.match(/^\/posts\/(\d+)$/)
      return match ? parseInt(match[1]) : null
    }
  },

  actions: {
    // 初始化設定
    init() {
      // 檢查是否顯示最新貼文按鈕
      const lastReadPost = localStorage.getItem('lastReadPost')
      this.showNewest = location.pathname === '/' && lastReadPost
    },
    
    // 獲取 CSRF Token
    getToken() {
      return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    },

    // 根據路徑設定 API 端點
    postlink() {
      if (location.pathname === '/') 
        this.posts_json = '/posts.json'
      else if (location.pathname.match(/^\/posts\/\d+$/))
        this.posts_json = location.pathname + '/comments.json'
      else if (location.pathname.match(/^\/users\/[\w_]+$/))
        this.posts_json = location.pathname + '/posts.json'
      else if (location.pathname.match(/^\/clubs\/\d+$/))
        this.posts_json = location.pathname + '/posts.json'
      else if (location.pathname.match(/^\/home\/likes$/))
        this.posts_json = '/home/likes.json'
      else if (location.pathname.match(/^\/home\/bookmarks$/))
        this.posts_json = '/home/bookmarks.json'
      else
        this.posts_json = '/posts.json'
    },

    // 處理貼文資料（新增相對時間）
    processPosts(posts) {
      return posts.map(post => {
        post.created_at_relative = timeAgo(post.created_at);
        return post;
      });
    },

    // 取得第一批貼文
    async getPosts() {
      // 預設載入最新貼文
      let lastReadPostId = 999999999
      
      try {
        const response = await axios.get(this.posts_json, {
          headers: { 'Accept': 'application/json' },
          params: { from: lastReadPostId + 10 }
        })

        this.posts = this.processPosts(response.data)
        this.maybeMore = response.data.length >= 15
        this.ready = true

        // 等待 DOM 更新完成後初始化功能
        nextTick(() => {
          this.createVideoPlayers()
          this.setupObserver()
          
          if (location.pathname === '/') {
            this.checkAndShowJumpNotification()
            this.setupScrollHandler()
          }
        })
      } catch (error) {
        console.error('載入貼文失敗:', error)
      }
    },

    // 載入更多貼文
    async morePosts() {
      if (this.posts.length === 0) return

      try {
        const response = await axios.get(this.posts_json, {
          params: { from: this.posts[this.posts.length - 1].id }
        })

        const newPosts = this.processPosts(response.data);
        this.posts.push(...newPosts)
        this.maybeMore = response.data.length >= 15

        nextTick(() => {
          this.createVideoPlayers()
          this.setupObserver()
          
          if (location.pathname === '/') {
            this.setupScrollHandler()
          }
        })
      } catch (error) {
        console.error('載入更多貼文失敗:', error)
      }
    },

    // 搜尋貼文 - 支援 URL 同步
    // @param keyword - 搜尋關鍵字
    // @param updateUrl - 是否更新瀏覽器 URL（預設 true）
    async searchPosts(keyword, updateUrl = true) {
      this.keyword = keyword
      this.posts = []
      this.ready = false

      // 更新瀏覽器 URL
      if (updateUrl) {
        const url = new URL(window.location)
        if (keyword) {
          url.searchParams.set('q', keyword)
        } else {
          url.searchParams.delete('q')
        }
        window.history.pushState({}, '', url)
      }

      try {
        const response = await axios.get('/posts.json', {
          params: { q: keyword },
          headers: { 'Accept': 'application/json' }
        })
        this.posts = this.processPosts(response.data)
        this.maybeMore = response.data.length >= 15
        this.ready = true
        nextTick(() => {
          this.createVideoPlayers()
        })
      } catch (error) {
        console.error('搜尋貼文失敗:', error)
        this.ready = true
      }
    },

    // 從 URL 初始化搜尋狀態
    // 用於頁面載入時檢查 URL 是否有 q 參數
    initFromUrl() {
      const params = new URLSearchParams(window.location.search)
      const q = params.get('q')
      if (q) {
        this.search = true
        this.keyword = q
        // 不更新 URL，避免重複
        this.searchPosts(q, false)
        return true
      }
      return false
    },

    // 切換貼文展開/收合
    showMore(postID) {
      // 尋找貼文：先在主列表中找，再在 currentPost 和 currentComments 中找
      let post = this.posts.find(post => post.id === postID);
      
      // 如果在主列表中找不到，檢查是否為當前正在查看的貼文
      if (!post && this.currentPost && this.currentPost.id === postID) {
        post = this.currentPost;
      }
      
      // 如果還是找不到，檢查是否在留言中
      if (!post) {
        post = this.currentComments.find(comment => comment.id === postID);
      }
      
      if (!post) return;

      // 如果 isExpanded 未定義，則初始化為 false
      if (post.isExpanded === undefined) {
        post.isExpanded = false;
      }
      
      // 切換展開狀態
      post.isExpanded = !post.isExpanded;
    },

    // 按讚功能
    async likePost(postID) {
      // 尋找貼文：先在主列表中找，再在 currentPost 和 currentComments 中找
      let post = this.posts.find(post => post.id === postID);
      
      // 如果在主列表中找不到，檢查是否為當前正在查看的貼文
      if (!post && this.currentPost && this.currentPost.id === postID) {
        post = this.currentPost;
      }
      
      // 如果還是找不到，檢查是否在留言中
      if (!post) {
        post = this.currentComments.find(comment => comment.id === postID);
      }
      
      if (!post) return

      try {
        await axios.post(`/posts/${postID}/like`, {
          authenticity_token: this.getToken()
        })

        if (post.isLiked) {
          post.likes--
          post.isLiked = false
        } else {
          post.likes++
          post.isLiked = true
        }
      } catch (error) {
        console.error('按讚操作失敗:', error)
      }
    },

    // 收藏功能
    async bookMark(postID) {
      // 尋找貼文：先在主列表中找，再在 currentPost 和 currentComments 中找
      let post = this.posts.find(post => post.id === postID);
      
      // 如果在主列表中找不到，檢查是否為當前正在查看的貼文
      if (!post && this.currentPost && this.currentPost.id === postID) {
        post = this.currentPost;
      }
      
      // 如果還是找不到，檢查是否在留言中
      if (!post) {
        post = this.currentComments.find(comment => comment.id === postID);
      }
      
      if (!post) return

      try {
        await axios.post(`/posts/${postID}/bookmark`, {
          authenticity_token: this.getToken()
        })

        post.isBookmarked = !post.isBookmarked
      } catch (error) {
        console.error('收藏操作失敗:', error)
      }
    },

    // 初始化視頻播放器
    createVideoPlayers() {
      this.posts.forEach((post, id) => {
        if (post.source === "redbook") {
          // 檢查 DOM 元素是否存在
          const element = document.getElementById(`mse-${post.id}`)
          if (element) {
            try {
              this.player[id] = new Player({
                id: `mse-${post.id}`,
                url: post.video,
                playsinline: true,
                whitelist: [""],
                fluid: true,
                poster: post.image,
                autoplay: false
              })
            } catch (error) {
              console.warn(`視頻播放器初始化失敗 (${post.id}):`, error.message)
            }
          } else {
            console.warn(`視頻容器尚未渲染: mse-${post.id}`)
          }
        }
      })
    },

    // 設定交叉觀察器（載入更多）
    setupObserver() {
      this.moreBtn = document.getElementById('moreBtn')
      if (!this.moreBtn) return

      if (this.observer) {
        this.observer.disconnect()
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.maybeMore) {
            this.morePosts()
          }
        })
      }, { 
        rootMargin: "0px", 
        threshold: 0.1 
      })

      this.observer.observe(this.moreBtn)
    },

    // 設定滾動處理器（記錄閱讀位置）
    setupScrollHandler() {
      const postListElement = document.querySelector('.posts-container')
      if (!postListElement) return

      let debounceTimer
      
      const scrollHandler = () => {
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          let currentPostId = ''
          
          for (let post of postListElement.children) {
            if (post.getBoundingClientRect().top <= 0) {
              currentPostId = post.id
            }
          }
          
          if (currentPostId) {
            localStorage.setItem('lastReadPost', currentPostId)
            localStorage.setItem('lastReadTime', new Date().toISOString())
          }
        }, 1000)
      }

      window.addEventListener('scroll', scrollHandler)
    },

    // 跳到上次閱讀位置
    jumpToLastRead() {
      const lastReadPost = localStorage.getItem('lastReadPost')
      if (lastReadPost) {
        const postElement = document.getElementById(lastReadPost)
        if (postElement) {
          postElement.scrollIntoView()
        }
      }
    },

    // 檢查並顯示跳轉通知
    checkAndShowJumpNotification() {
      const lastReadPost = localStorage.getItem('lastReadPost')
      const lastReadTime = localStorage.getItem('lastReadTime')
      
      if (lastReadPost && lastReadTime) {
        const readTime = new Date(lastReadTime)
        const timeString = this.formatReadTime(readTime)
        
        this.jumpNotification = {
          id: 'jump-to-last-read',
          type: 'info',
          title: '繼續閱讀',
          message: `上次在${timeString}閱讀的進度還在`,
          autoClose: false,
          actions: [
            {
              key: 'jump',
              type: 'primary',
              text: '跳到那裡',
              icon: 'fas fa-arrow-up',
              close: true
            },
            {
              key: 'dismiss',
              type: 'secondary', 
              text: '不用了',
              close: true
            }
          ]
        }
        this.showJumpNotification = true
      }
    },

    // 格式化閱讀時間
    formatReadTime(date) {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
      const readDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const timeStr = `${hours}:${minutes}`
      
      if (readDate.getTime() === today.getTime()) {
        return `今天 ${timeStr}`
      } else if (readDate.getTime() === yesterday.getTime()) {
        return `昨天 ${timeStr}`
      } else {
        const month = date.getMonth() + 1
        const day = date.getDate()
        return `${month}月${day}日 ${timeStr}`
      }
    },

    // 跳轉到上次閱讀位置
    jumpToLastReadPosition() {
      this.jumpToLastRead()
      this.dismissJumpNotification()
    },

    // 關閉跳轉通知
    dismissJumpNotification() {
      this.showJumpNotification = false
      this.jumpNotification = null
    },

    // 處理通知操作
    handleNotificationAction({ notificationId, actionKey }) {
      if (notificationId === 'jump-to-last-read') {
        switch (actionKey) {
          case 'jump':
            this.jumpToLastReadPosition()
            break
          case 'dismiss':
            this.dismissJumpNotification()
            break
        }
      }
    },

    // 載入單篇貼文詳情
    async loadPostDetail(postId) {
      this.isLoadingDetail = true
      try {
        const response = await axios.get(`/posts/${postId}.json`, {
          headers: { 'Accept': 'application/json' }
        })
        
        const post = response.data
        post.created_at_relative = timeAgo(post.created_at)
        // 在貼文詳情頁預設展開完整內容
        post.isExpanded = true
        this.currentPost = post
      } catch (error) {
        console.error('載入貼文詳情失敗:', error)
        this.currentPost = null
      } finally {
        this.isLoadingDetail = false
      }
    },

    // 載入貼文留言
    async loadPostComments(postId) {
      try {
        const response = await axios.get(`/posts/${postId}/comments.json`, {
          headers: { 'Accept': 'application/json' }
        })
        
        this.currentComments = this.processPosts(response.data)
      } catch (error) {
        console.error('載入留言失敗:', error)
        this.currentComments = []
      }
    },

    // 提交留言
    async submitComment(postId, content) {
      try {
        const response = await axios.post('/posts', {
          post: {
            long: content,
            parent_id: postId
          },
          authenticity_token: this.getToken()
        }, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        })
        
        return response.data
      } catch (error) {
        console.error('提交留言失敗:', error)
        throw error
      }
    },

    // 清理單篇貼文相關狀態
    clearPostDetail() {
      this.currentPost = null
      this.currentComments = []
      this.isLoadingDetail = false
    },

    // 清理貼文列表
    clearPosts() {
      this.posts = []
      this.maybeMore = true
      this.showNewest = false
    },

    // 更新當前路徑（觸發響應性）
    updateCurrentPath(path) {
      this.currentPath = path
    },

    // 清理資源
    cleanup() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      
      // 清理播放器
      this.player.forEach(player => {
        if (player && player.destroy) {
          player.destroy()
        }
      })
      this.player = []
      
      // 清理貼文詳情
      this.clearPostDetail()
    },

    // 新增貼文到列表頂端
    addPost(post) {
      const processedPost = this.processPosts([post])[0];
      this.posts.unshift(processedPost);
    },

    // 更新列表中的特定貼文
    updatePost(updatedPost) {
      const index = this.posts.findIndex(p => p.id === updatedPost.id);
      if (index !== -1) {
        const processedPost = this.processPosts([updatedPost])[0];
        this.posts[index] = processedPost;
      }
    },

    // 切換搜尋欄可見度
    toggleSearch() {
      this.search = !this.search;
      if (!this.search) {
        this.keyword = '';
        // 清除 URL 的搜尋參數
        const url = new URL(window.location);
        if (url.searchParams.has('q')) {
          url.searchParams.delete('q');
          window.history.pushState({}, '', url);
        }
      }
    },

    // 清除搜尋並重新載入貼文
    async clearSearch() {
      this.search = false;
      this.keyword = '';

      // 清除 URL 的搜尋參數
      const url = new URL(window.location);
      if (url.searchParams.has('q')) {
        url.searchParams.delete('q');
        window.history.pushState({}, '', url);
      }

      // 重新載入貼文
      this.posts = [];
      this.maybeMore = true;
      this.postlink();
      await this.getPosts();
    },

    // 刪除貼文
    async deletePost(postId) {
      try {
        await axios.delete(`/posts/${postId}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.getToken()
          }
        })

        // 從主列表移除
        const postIndex = this.posts.findIndex(p => p.id === postId)
        if (postIndex !== -1) {
          this.posts.splice(postIndex, 1)
        }

        // 從留言列表移除
        const commentIndex = this.currentComments.findIndex(c => c.id === postId)
        if (commentIndex !== -1) {
          this.currentComments.splice(commentIndex, 1)
        }

        // 如果刪除的是正在查看的主貼文，則導向首頁
        if (this.currentPost && this.currentPost.id === postId) {
          this.clearPostDetail()
          // 導航到首頁
          window.location.href = '/'
        }
      } catch (error) {
        console.error('刪除貼文失敗:', error)
        // 可以在這裡加入錯誤提示
        alert('刪除失敗，您可能沒有權限或貼文不存在。')
      }
    },

    // 置頂貼文
    async pinPost(postId) {
      try {
        const response = await axios.patch(`/posts/${postId}/pin`, {}, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.getToken()
          }
        })

        // 更新主列表中的貼文狀態
        const postIndex = this.posts.findIndex(p => p.id === postId)
        if (postIndex !== -1) {
          this.posts[postIndex].pinned = true
        }

        // 更新當前查看的貼文狀態
        if (this.currentPost && this.currentPost.id === postId) {
          this.currentPost.pinned = true
        }

        return response.data
      } catch (error) {
        console.error('置頂貼文失敗:', error)
        throw error
      }
    },

    // 取消置頂貼文
    async unpinPost(postId) {
      try {
        const response = await axios.delete(`/posts/${postId}/unpin`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.getToken()
          }
        })

        // 更新主列表中的貼文狀態
        const postIndex = this.posts.findIndex(p => p.id === postId)
        if (postIndex !== -1) {
          this.posts[postIndex].pinned = false
        }

        // 更新當前查看的貼文狀態
        if (this.currentPost && this.currentPost.id === postId) {
          this.currentPost.pinned = false
        }

        return response.data
      } catch (error) {
        console.error('取消置頂失敗:', error)
        throw error
      }
    }
  }
})
